/**
 * 開封・クリック計測の表示トークンと対応表.
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md` 3.4（色の規約）と
 * `open-click-analytics.html` 1 章（図1〜図7）が正典。
 *
 * 色の規約（md 3.4）:
 *  1. 反応スケールは 6 段（グレー = 反応なし → 青 → 水色 → 薄橙 → 橙 → 赤 = 返信）。
 *     薄い 2 段（開封 複数回 / クリック 1 回）は面にのみ使う。
 *  2. 強さは色ではなくバーの長さとラベル文字で二重に符号化する。
 *  3. 「要対応」に色は使わない（濃いスレートの縦線 + ⚑ + 太字）。
 *  4. 内訳の横帯（デバイス別・会社の状態）は反応スケールの外でインディゴ 3 段。
 *  5. 反応スケールでは緑と黄を使わない（6 色に別の色相を割り当てると、
 *     一覧のバーとグラフの系列で同じ色相が別の意味を持つため）。
 *
 * 率の KPI の数値に色を付けないのは md 9.0（3.4 ではない）。
 */

/** 反応の段（1 = 反応なし 〜 6 = 返信） */
export type ReactionStep = 1 | 2 | 3 | 4 | 5 | 6;

export type ReactionStepToken = {
  /** バー・縦線・ドットに使う色 */
  color: string;
  /** 行ティント（1 段目は塗らないため null） */
  tint: string | null;
  /** 反応バーの伸び幅（%）。下 5 段は 16% ずつ等間隔、返信だけ 100% */
  width: number;
};

/** 反応スケール 6 段（html 1.3 の凡例が正典） */
export const REACTION_SCALE: Record<ReactionStep, ReactionStepToken> = {
  1: { color: "#cbd5e1", tint: null, width: 16 },
  2: { color: "#5E93C9", tint: "#EAF1F8", width: 32 },
  3: { color: "#A9CCE8", tint: "#F2F8FC", width: 48 },
  4: { color: "#F0C09A", tint: "#FDF5EE", width: 64 },
  5: { color: "#D07A45", tint: "#FAEDE1", width: 80 },
  6: { color: "#CE7070", tint: "#FAEFEF", width: 100 },
};

/** 反応スケールの外: 内訳の横帯（インディゴ 3 段） */
export const BREAKDOWN_BAND_COLORS = ["#6B72C4", "#B6BAE4", "#cbd5e1"] as const;

/** 内訳の横帯の上に載せる文字色（濃い帯 = 白 / 淡い帯 = 暗色 / 灰 = 暗色） */
export const BREAKDOWN_BAND_TEXT_COLORS = [
  "#ffffff",
  "#312e81",
  "#1f2733",
] as const;

/** 要対応（色ではなく形で示すための濃いスレート） */
export const ATTENTION_COLOR = "#334155";

/** データが無い / 計測対象外の `—` に使う灰 */
export const NO_DATA_COLOR = "#94a3b8";

/** 不達・送信失敗の行（届いていない宛先）の背景 */
export const DEAD_ROW_BACKGROUND = "#f8fafc";

/** 不達・送信失敗の行の左端の縦線 */
export const DEAD_ROW_BORDER = "#e2e8f0";

// ============================================================
// グラフの下地（軸・罫線・ラベル・ツールチップ）
// ============================================================
//
// 反応スケール・「要対応」・「計測対象外」とは**意味が別**なので、値が同じでも
// 別トークンにする（反応スケールの色を調整したときに、無関係なグラフの罫線や
// ツールチップまで巻き添えで動くのを防ぐ）。

/** 軸線（x 軸・y 軸） */
export const CHART_AXIS_LINE = "#cbd5e1";

/** 内側の罫線（軸線より淡い） */
export const CHART_GRID_LINE = "#f1f5f9";

/** 目盛ラベル・軸の補助文 */
export const CHART_AXIS_LABEL = "#94a3b8";

/** 横軸の項目ラベル・凡例の文字（目盛ラベルより濃い） */
export const CHART_LABEL = "#475569";

/**
 * 罫線と目盛ラベルの位置（軸上限に対する比）。
 * **罫線とラベルは必ずこの同じ配列から引く** —— 別々のリテラルにすると
 * 片方だけ編集したときに、ラベルが罫線と違う値を指しても誰も気付かない。
 * 上端（比 1）には罫線を引かない（y 軸の頂点そのものなので）。
 */
export const CHART_AXIS_TICK_RATIOS = [0.25, 0.5, 0.75, 1] as const;

/** 罫線を引く比（上端を除いた内側の 3 本） */
export const CHART_GRID_RATIOS = CHART_AXIS_TICK_RATIOS.filter(
  ratio => ratio < 1,
);

/**
 * グラフの y 軸帯の幅（座標系）。目盛ラベル・軸見出し「率」・y 軸の縦線が
 * この中に収まる。
 *
 * 目盛ラベルがこの幅に収まるかは `axisTickLabel`（`utils/delivery-analytics`）の
 * 責務で、その単体テストがここを参照する。グラフ本体（React コンポーネント）に
 * 置くと util のテストが描画まで引き込むため、下地のトークンと同じここに置く。
 */
export const AXIS_WIDTH = 42;

/**
 * 目盛ラベルのベースラインを、そのラベルが指す線の中心に合わせる下げ幅（座標系）。
 *
 * svg の `<text>` は `y` をベースライン（文字の足元）に取るので、線と同じ `y` を
 * 渡すとラベルが線の上に浮く。文字サイズ `CHART_AXIS_LABEL_FONT_SIZE` の
 * おおよそ 1/3 が視覚的な中心になる。
 */
export const CHART_TICK_LABEL_BASELINE_OFFSET = 3;

/** 目盛ラベル・軸見出しの文字サイズ（座標系） */
export const CHART_AXIS_LABEL_FONT_SIZE = 9;

/** ツールチップの箱 */
export const CHART_TOOLTIP_BACKGROUND = "#1f2733";

/** ツールチップの文字（注目している行） */
export const CHART_TOOLTIP_TEXT = "#ffffff";

/** ツールチップの文字（注目していない行） */
export const CHART_TOOLTIP_TEXT_DIM = "#cbd5e1";

/** 開閉三角の色（開＝濃いスレート / 閉＝淡いスレート）。開閉の状態を示すだけで、
 *  「要対応」「計測対象外」とは意味が別 */
export const DISCLOSURE_COLOR = {
  open: "#334155",
  closed: "#94a3b8",
} as const;

// ============================================================
// 会社の状態・関心度（html 図6 / 「次の一手」の出し方）
// ============================================================

/** 会社の状態（API は英語スネークで返す） */
export type CompanyState =
  | "engaged"
  | "lapsed"
  | "never_responded"
  | "not_delivered";

/** 関心度（「まだ送っていない」会社は関心度を持たないため null） */
export type InterestLevel = "high" | "medium" | "low" | "lapsed" | "none";

/** 推移ドット列の 1 区間の反応 */
export type DotReaction = "none" | "open" | "click" | "reply";

/**
 * 会社の状態の日本語ラベル（「配信していない」の UI 表記は「まだ送っていない」）。
 * @param {CompanyState} state 状態
 * @return {string} 表示ラベル
 */
export function companyStateLabel(state: CompanyState): string {
  switch (state) {
    case "engaged":
      return "やりとりあり";
    case "lapsed":
      return "途絶えた";
    case "never_responded":
      return "一度も反応なし";
    default:
      return "まだ送っていない";
  }
}

export type InterestChipToken = {
  label: string;
  /** 丸の色 */
  dot: string;
  /** 文字色（反応スケールの暗色化派生） */
  text: string;
  /** ⚑ を付けるか（要対応の記法） */
  flag: boolean;
};

/**
 * 関心度チップの表示トークン（html 図6 の figcaption が正典）。
 * 高 / 中 / 低 は反応スケール、「途絶えた」「一度も反応なし」はスケールの外で
 * どちらも `#94a3b8`。⚑ の有無と文字色で区別する。
 */
export const INTEREST_CHIP: Record<InterestLevel, InterestChipToken> = {
  high: { label: "高", dot: "#CE7070", text: "#A85555", flag: false },
  medium: { label: "中", dot: "#D07A45", text: "#A65C31", flag: false },
  low: { label: "低", dot: "#5E93C9", text: "#3F6E9E", flag: false },
  lapsed: {
    label: "途絶えた",
    dot: NO_DATA_COLOR,
    text: ATTENTION_COLOR,
    flag: true,
  },
  none: {
    label: "一度も反応なし",
    dot: NO_DATA_COLOR,
    text: "#64748b",
    flag: false,
  },
};

/** 推移ドット列の 1 段（色と高さの両方で段を示す） */
export const ACTIVITY_DOT: Record<
  DotReaction,
  { color: string; height: number }
> = {
  none: { color: "#cbd5e1", height: 3 },
  open: { color: "#5E93C9", height: 6 },
  click: { color: "#D07A45", height: 9 },
  reply: { color: "#CE7070", height: 12 },
};

/** 「次の一手」の遷移先 */
export type NextActionTarget = "replies" | "company";

export type NextAction = {
  text: string;
  target: NextActionTarget;
};

/**
 * 「次の一手」（LLM は使わず、関心度から一意に決まる定型文）。
 * 対応表は html「次の一手」の出し方が正典。
 * @param {InterestLevel | null} interestLevel 関心度（まだ送っていない会社は null）
 * @return {NextAction} 文言と遷移先
 */
export function nextActionFor(interestLevel: InterestLevel | null): NextAction {
  switch (interestLevel) {
    case "high":
      return { text: "返信内容を確認して商談化する", target: "replies" };
    case "medium":
      return {
        text: "クリックされた資料・ページに触れて連絡する",
        target: "company",
      };
    case "low":
      return { text: "別の切り口で再アプローチする", target: "company" };
    case "lapsed":
      return { text: "担当者の異動・取引停止を確認する", target: "company" };
    case "none":
      return { text: "配信先リストを見直す", target: "company" };
    default:
      return { text: "次の配信に含める", target: "company" };
  }
}

// ============================================================
// 宛先の状態（html 図3 直下の判定表）
// ============================================================

/**
 * 状態列の値。`pending` はまだ送信を試みていない宛先（下書き・予約中の配信で
 * 作成時に作られる行）で、送信失敗とは分ける（設計書 9.1 の判定表）。
 */
export type DeliveryState =
  | "pending"
  | "sent"
  | "undelivered"
  | "complained"
  | "failed";

/** 届かなかった宛先の 4 分類 */
export type ActionCategory =
  | "delete_candidate"
  | "needs_check"
  | "no_action"
  | "sender_action";

/** 状態列の日本語ラベル */
export const DELIVERY_STATE_LABEL: Record<DeliveryState, string> = {
  pending: "待機",
  sent: "送信",
  undelivered: "不達",
  complained: "苦情",
  failed: "送信失敗",
};

/** 苦情の宛先に添える一文（届いた事実のラベル） */
export const COMPLAINT_MESSAGE = "迷惑メールとして報告されました";

/**
 * `(bounceType, bounceSubType)` の組に対する一覧の文言。
 * 営業には Permanent / Transient / General の語を見せない。
 */
const BOUNCE_MESSAGES: ReadonlyArray<{
  bounceType: string;
  detailTypes: ReadonlyArray<string>;
  message: string;
}> = [
  {
    bounceType: "Permanent",
    detailTypes: ["NoEmail"],
    message:
      "このアドレスは存在しません。別の担当者の連絡先に差し替えてください",
  },
  {
    bounceType: "Permanent",
    detailTypes: ["General"],
    message:
      "このアドレスには届きませんでした。今後も届かない見込みです。別の担当者の連絡先に差し替えてください",
  },
  {
    bounceType: "Permanent",
    detailTypes: ["Suppressed"],
    message:
      "過去に届かなかった記録があるため、メール配信システム側で送信が止められています。解除するまでこのアドレスには届きません（解除が必要な場合は運用担当へ依頼してください）",
  },
  {
    bounceType: "Permanent",
    detailTypes: ["OnAccountSuppressionList", "OnTenantSuppressionList"],
    message:
      "メール配信システムの送信停止リストに登録されているため送信されていません（過去の不達・迷惑メール報告、または手動登録）。解除が必要な場合は運用担当へ依頼してください",
  },
  {
    bounceType: "Transient",
    detailTypes: ["General"],
    message: "一時的に届きませんでした（次回は届く可能性があります）",
  },
  {
    bounceType: "Transient",
    detailTypes: ["MailboxFull"],
    message: "受信者のメールボックスが満杯です（次回は届く可能性があります）",
  },
  {
    bounceType: "Transient",
    detailTypes: ["MessageTooLarge"],
    message:
      "メールの容量が大きすぎて届きませんでした（添付を減らすと届きます）",
  },
  {
    bounceType: "Transient",
    detailTypes: ["AttachmentRejected"],
    message: "添付ファイルが受信側に拒否されました（添付を外すと届きます）",
  },
  {
    bounceType: "Transient",
    detailTypes: ["ContentRejected"],
    message: "本文の内容が受信側に拒否されました（文面の見直しが必要です）",
  },
  {
    bounceType: "Transient",
    detailTypes: ["CustomTimeoutExceeded"],
    message: "一時的に届きませんでした（受信側の応答待ちで時間切れ）",
  },
];

/** 送信失敗（`sent_at` が空）の文言。`error_message` の内容で出し分ける */
export const SEND_FAILURE_MESSAGES = {
  /** error_message がある */
  withError: "送信時にエラーが発生しました（再送してください）",
  /** 安全フィルタでスキップされた */
  safetyFilter:
    "送信対象外のドメインのため送信していません（設定を確認してください）",
  /** error_message も無い（タイムガードで打ち切られた宛先） */
  aborted: "送信されませんでした（処理が中断されました。再送してください）",
} as const;

/** 安全フィルタでスキップされたことを示す `error_message` */
export const SAFETY_FILTER_ERROR =
  "Email domain not in allowed list (safety filter)";

/**
 * 対応表にある `(bounceType, detailType)` の文言を引く。
 * @param {string | null} bounceType bounceType
 * @param {string | null} detailType bounceSubType 等の理由コード
 * @return {string | null} 文言。対応表に無ければ null
 */
export function knownBounceMessage(
  bounceType: string | null,
  detailType: string | null,
): string | null {
  if (!bounceType || !detailType) return null;
  const hit = BOUNCE_MESSAGES.find(
    row =>
      row.bounceType === bounceType && row.detailTypes.includes(detailType),
  );
  return hit ? hit.message : null;
}
